import React, { useState, useEffect, Fragment } from 'react';
import { Plus, Trash2, Pencil, DollarSign, Tag, ChevronDown, Check, Brain } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Combobox } from '@headlessui/react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useBudgetStore, useCurrencyStore, useTransactionStore, useNotificationStore, createBudgetNotification } from '@flows.cash/core';
import { useAuthStore } from '@/store/authStore';
import { useCategoriesStore } from '@/store/categoriesStore';
import { useAccountsStore } from '@/store/accountsStore';
import { useCurrencyConversion } from '@/hooks/useCurrencyConversion';
import { useCurrenciesStore } from '@flows.cash/core';
import { cn } from '@/lib/utils';
import { endOfMonth } from 'date-fns';
import { toast } from 'sonner';
import { Budget, BudgetPeriod } from '@flows.cash/types';
import { Card, CardContent } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { format } from 'date-fns';

type BudgetStatus = 'safe' | 'warning' | 'danger';

interface BudgetFormData {
  categoryId: string;
  category: string;
  amount: number;
  period: BudgetPeriod;
  currency: string;
  startDate: Date;
  endDate: Date;
}

interface BudgetCalculation {
  spent: number;
  remaining: number;
  percentage: number;
  status: BudgetStatus;
  periodLabel: string;
  startDate: Date;
  endDate: Date;
}

// Extended budget type to match our actual Supabase schema
interface ExtendedBudget extends Budget {
  // No need for category_ids anymore since we're using category_id
}

interface BudgetManagerProps {
  hideHeader?: boolean;
  searchQuery?: string;
  periodFilter?: BudgetPeriod;
  onCreateBudget?: () => void;
  isAddModalOpen?: boolean;
  onAddModalOpenChange?: (open: boolean) => void;
  onOpenSmartAssistant?: () => void;
}

export default function BudgetManager({ 
  hideHeader = false, 
  searchQuery = '', 
  periodFilter, 
  onCreateBudget,
  isAddModalOpen: externalIsAddModalOpen,
  onAddModalOpenChange,
  onOpenSmartAssistant
}: BudgetManagerProps) {
  const { budgets, isLoading: budgetsLoading, fetchBudgets, addBudget, updateBudget, deleteBudget, setShouldFetch } = useBudgetStore();
  const { accounts, loading: accountsLoading, fetchAccounts } = useAccountsStore();
  const { categories, fetchCategories } = useCategoriesStore();
  const { transactions, fetchTransactions } = useTransactionStore();
  const { defaultCurrency, formatAmount } = useCurrencyConversion();
  const { convertAmount, rates, fetchRates } = useCurrencyStore();
  const { user } = useAuthStore();
  const { addNotification } = useNotificationStore();
  const { currencies, fetchCurrencies } = useCurrenciesStore();
  const [internalIsAddModalOpen, setInternalIsAddModalOpen] = useState(false);
  const [editingBudget, setEditingBudget] = useState<ExtendedBudget | null>(null);
  const [budgetCalculations, setBudgetCalculations] = useState<Record<string, BudgetCalculation>>({});
  const [formData, setFormData] = useState<BudgetFormData>({
    categoryId: '',
    category: '',
    amount: 0,
    period: 'monthly',
    currency: defaultCurrency,
    startDate: new Date(),
    endDate: endOfMonth(new Date()),
  });
  const [categoryQuery, setCategoryQuery] = useState('');
  const [isCategoryOpen, setIsCategoryOpen] = useState(false);

  // Track which budgets have been notified to prevent duplicates
  const notifiedBudgets = React.useRef<Record<string, { warning: boolean, exceeded: boolean }>>({});

  // Use external modal state if provided, otherwise use internal state
  const isAddModalOpen = externalIsAddModalOpen !== undefined ? externalIsAddModalOpen : internalIsAddModalOpen;
  
  // Handle modal state changes
  const setIsAddModalOpen = (open: boolean) => {
    if (onAddModalOpenChange) {
      onAddModalOpenChange(open);
    } else {
      setInternalIsAddModalOpen(open);
    }
    
    // Reset form when closing the modal
    if (!open) {
      setEditingBudget(null);
      resetForm();
    }
  };

  useEffect(() => {
    if (user?.id) {
      setShouldFetch(true);
      fetchBudgets(user.id);
      fetchCategories(user.id);
      fetchTransactions(user.id);
      fetchAccounts(user.id);
      fetchCurrencies(user.id);
      fetchRates(); // Fetch exchange rates
    }
    return () => {
      setShouldFetch(false);
    };
  }, [user?.id, setShouldFetch, fetchBudgets, fetchCategories, fetchTransactions, fetchAccounts, fetchCurrencies, fetchRates]);

  useEffect(() => {
    console.log('Categories loaded:', categories.length);
  }, [categories]);

  const getBudgetDateRange = (period: BudgetPeriod, date: Date = new Date()): { startDate: Date, endDate: Date } => {
    const startDate = new Date(date);
    const endDate = new Date(date);

    switch (period) {
      case 'daily':
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(23, 59, 59, 999);
        break;
      case 'weekly':
        const day = startDate.getDay();
        startDate.setDate(startDate.getDate() - day);
        endDate.setDate(startDate.getDate() + 6);
        break;
      case 'monthly':
        startDate.setDate(1);
        endDate.setMonth(endDate.getMonth() + 1);
        endDate.setDate(0);
        break;
      case 'yearly':
        startDate.setMonth(0, 1);
        endDate.setMonth(11, 31);
        break;
    }

    return { startDate, endDate };
  };

  const getPeriodLabel = (period: BudgetPeriod, date: Date): string => {
    switch (period) {
      case 'daily':
        return format(date, 'MMMM d, yyyy');
      case 'weekly':
        const { startDate, endDate } = getBudgetDateRange(period, date);
        return `${format(startDate, 'MMM d')} - ${format(endDate, 'MMM d, yyyy')}`;
      case 'monthly':
        return format(date, 'MMMM yyyy');
      case 'yearly':
        return format(date, 'yyyy');
      default:
        return '';
    }
  };

  const getBudgetStatus = (percentage: number): BudgetStatus => {
    if (percentage >= 100) return 'danger';
    if (percentage >= 80) return 'warning';
    return 'safe';
  };

  const calculateBudget = async (budget: ExtendedBudget): Promise<BudgetCalculation> => {
    const currentDate = new Date();
    const { startDate, endDate } = getBudgetDateRange(budget.period, currentDate);
    
    // Get category ID from the budget
    const categoryId = budget.category_id || budget.categoryId;
    
    // Find the category and its parent if it's a subcategory
    let selectedCategory = null;
    let parentCategory = null;
    let categoryIds: string[] = [];
    let isOverallBudget = false;

    // Handle overall budget (no category specified)
    if (!categoryId) {
      isOverallBudget = true;
      // For overall budgets, we'll track all expense categories
      categoryIds = [];
      categories.forEach(category => {
        categoryIds.push(category.id);
        if (category.subcategories) {
          categoryIds.push(...category.subcategories.map(sub => sub.id));
        }
      });
    } else {
      // Search through categories to find the selected one
      for (const category of categories) {
        if (category.id === categoryId) {
          selectedCategory = category;
          // If this is a main category, include all its subcategory IDs
          categoryIds = [category.id, ...(category.subcategories?.map(sub => sub.id) || [])];
          break;
        }
        // Check subcategories
        if (category.subcategories) {
          const subcategory = category.subcategories.find(sub => sub.id === categoryId);
          if (subcategory) {
            selectedCategory = subcategory;
            parentCategory = category;
            // If this is a subcategory, only use its own ID
            categoryIds = [subcategory.id];
            break;
          }
        }
      }

      // Skip budget if category not found and it's not an overall budget
      if (!selectedCategory && !isOverallBudget) {
        console.error('Category not found for budget:', budget.id);
        return {
          spent: 0,
          remaining: budget.amount,
          percentage: 0,
          status: 'safe',
          periodLabel: getPeriodLabel(budget.period, currentDate),
          startDate,
          endDate,
        };
      }
    }
    
    // Calculate spent amount in the budget's currency
    let totalSpent = 0;
    const filteredTransactions = transactions.filter(tx => {
      // For overall budgets, include all expense transactions
      if (isOverallBudget) {
        return tx.type === 'expense' &&
          new Date(tx.date) >= startDate &&
          new Date(tx.date) <= endDate;
      }
      
      // For category-specific budgets, match only transactions in those categories
      return tx.type === 'expense' &&
        tx.category_id && // Make sure category_id exists
        categoryIds.includes(tx.category_id) && // Check against all relevant category IDs
        new Date(tx.date) >= startDate &&
        new Date(tx.date) <= endDate;
    });

    for (const tx of filteredTransactions) {
      const account = accounts.find(a => a.id === tx.account_id);
      if (account) {
        try {
          // For expenses, the amount is stored as negative in the database
          // We need to convert it to positive for budget calculations
          const txAmount = Math.abs(tx.amount);
          
          // Only convert if currencies are different
          let amount = txAmount;
          if (account.currency !== budget.currency) {
            try {
              amount = convertAmount(txAmount, account.currency, budget.currency);
              // If conversion returns 0 or null due to missing exchange rates, use original amount
              if (!amount) {
                console.warn(`Could not convert ${txAmount} from ${account.currency} to ${budget.currency}. Using original amount.`);
                amount = txAmount;
              }
            } catch (error) {
              console.warn(`Error converting amount from ${account.currency} to ${budget.currency}:`, error);
              amount = txAmount;
            }
          }
        totalSpent += amount;
        } catch (error) {
          console.warn(`Error processing transaction ${tx.id}:`, error);
          // If processing fails, use the original amount
          totalSpent += Math.abs(tx.amount);
        }
      }
    }

    const remaining = budget.amount - totalSpent;
    const percentage = (totalSpent / budget.amount) * 100;
    const status = getBudgetStatus(percentage);

    // Create notifications based on status
    if (status === 'danger') {
      // Get category name with parent if it's a subcategory
      const categoryName = isOverallBudget
        ? budget.name || 'Overall Budget'
        : parentCategory 
          ? `${parentCategory.name} → ${selectedCategory!.name}`
          : selectedCategory!.name;
      
      // Initialize tracking for this budget if not exists
      if (!notifiedBudgets.current[budget.id]) {
        notifiedBudgets.current[budget.id] = { warning: false, exceeded: false };
      }
      
      // Only create notification if not already notified for exceeded status
      if (!notifiedBudgets.current[budget.id].exceeded) {
        // Create budget notification using the core system
        await createBudgetNotification({
          type: 'exceeded',
          data: {
            budgetName: budget.name,
            category: categoryName,
            currentSpending: totalSpent,
            budgetLimit: budget.amount,
            currency: budget.currency,
            percentage: Math.round(percentage)
          }
        });
        
        // Also create in-app notification
        addNotification({
          title: 'notifications.budget.exceeded.title',
          message: 'notifications.budget.exceeded.message',
          type: 'error',
          data: { 
            budgetId: budget.id,
            name: categoryName,
            amount: budget.amount,
            spent: totalSpent
          }
        });
        
        // Mark as notified
        notifiedBudgets.current[budget.id].exceeded = true;
      }
      
      // Removed updateBudget call to prevent infinite loop
    } else if (status === 'warning') {
      // Get category name with parent if it's a subcategory
      const categoryName = isOverallBudget
        ? budget.name || 'Overall Budget'
        : parentCategory 
          ? `${parentCategory.name} → ${selectedCategory!.name}`
          : selectedCategory!.name;
      
      // Initialize tracking for this budget if not exists
      if (!notifiedBudgets.current[budget.id]) {
        notifiedBudgets.current[budget.id] = { warning: false, exceeded: false };
      }
      
      // Only create notification if not already notified for warning status
      if (!notifiedBudgets.current[budget.id].warning) {
        // Create budget notification using the core system
        await createBudgetNotification({
          type: 'approaching',
          data: {
            budgetName: budget.name,
            category: categoryName,
            currentSpending: totalSpent,
            budgetLimit: budget.amount,
            currency: budget.currency,
            percentage: Math.round(percentage)
          }
        });
        
        // Also create in-app notification
        addNotification({
          title: 'notifications.budget.warning.title',
          message: 'notifications.budget.warning.message',
          type: 'warning',
          data: { 
            budgetId: budget.id,
            name: categoryName,
            percentage: Math.round(percentage * 100),
            amount: budget.amount,
            spent: totalSpent
          }
        });
        
        // Mark as notified
        notifiedBudgets.current[budget.id].warning = true;
      }
      
      // Removed updateBudget call to prevent infinite loop
    }

    return {
      spent: totalSpent,
      remaining,
      percentage,
      status,
      periodLabel: getPeriodLabel(budget.period, currentDate),
      startDate,
      endDate,
    };
  };

  useEffect(() => {
    const updateCalculations = async () => {
      if (!rates) {
        console.log('Waiting for exchange rates to be available...');
        return;
      }

      const calculations: Record<string, BudgetCalculation> = {};
      for (const budget of budgets) {
        if (budget.id) {
          calculations[budget.id] = await calculateBudget(budget as ExtendedBudget);
        }
      }
      setBudgetCalculations(calculations);
    };

    if (budgets.length > 0 && transactions.length > 0 && accounts.length > 0) {
      updateCalculations();
    }
  }, [budgets, transactions, accounts, rates, convertAmount]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.id) return;

    if (!formData.categoryId) {
      toast.error("Please select a category");
      return;
    }

    if (!formData.amount || formData.amount <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }

    try {
      const now = new Date();
      
      // Find the selected category and its parent if it's a subcategory
      let selectedCategory = null;
      let parentCategory = null;

      // Search through categories to find the selected one
      for (const category of categories) {
        if (category.id === formData.categoryId) {
          selectedCategory = category;
          break;
        }
        // Check subcategories
        if (category.subcategories) {
          const subcategory = category.subcategories.find(sub => sub.id === formData.categoryId);
          if (subcategory) {
            selectedCategory = subcategory;
            parentCategory = category;
            break;
          }
        }
      }

      if (!selectedCategory) {
        toast.error("Selected category not found");
        return;
      }

      if (editingBudget) {
        const updateData = {
          name: parentCategory 
            ? `${parentCategory.name} → ${selectedCategory.name}`
            : selectedCategory.name,
          amount: formData.amount,
          currency: formData.currency,
          category_id: selectedCategory.id,
          period: formData.period,
          start_date: formData.startDate.toISOString(),
          end_date: formData.endDate.toISOString(),
          updated_at: now.toISOString()
        };

        await updateBudget(user.id, editingBudget.id, updateData);
        toast.success("Budget updated successfully");
      } else {
        const timestamp = now.toISOString();
        
        // Create a budget object that includes only fields that exist in Supabase schema
        const supabaseBudgetData = {
          user_id: user.id,
          name: parentCategory 
            ? `${parentCategory.name} → ${selectedCategory.name}`
            : selectedCategory.name,
        amount: formData.amount,
        currency: formData.currency,
          category_id: selectedCategory.id,
        period: formData.period,
          start_date: formData.startDate.toISOString(),
          end_date: formData.endDate.toISOString(),
        spent: 0,
        remaining: formData.amount,
          is_archived: false,
          created_at: timestamp,
          updated_at: timestamp
        };
        
        await addBudget(user.id, supabaseBudgetData as any);
        toast.success("Budget created successfully");
      }
      
      setIsAddModalOpen(false);
      setEditingBudget(null);
      resetForm();
    } catch (error) {
      toast.error("Failed to save budget");
      console.error('Error saving budget:', error);
    }
  };

  const handleEdit = (budget: ExtendedBudget) => {
    try {
      // Helper function to parse date safely
      const safeParseDate = (dateStr: string | undefined): Date => {
        if (!dateStr) return new Date();
        try {
          const parsedDate = new Date(dateStr);
          // Check if date is valid
          if (isNaN(parsedDate.getTime())) {
            console.warn('Invalid date string:', dateStr);
            return new Date();
          }
          return parsedDate;
        } catch (error) {
          console.warn('Error parsing date:', error);
          return new Date();
        }
      };

      // Get categoryId from the direct fields
      const categoryId = budget.categoryId || budget.category_id || '';

    setFormData({
        categoryId,
        category: budget.name,
      amount: budget.amount,
      period: budget.period,
      currency: budget.currency,
        startDate: safeParseDate(budget.startDate || budget.start_date),
        endDate: safeParseDate(budget.endDate || budget.end_date),
    });
    setEditingBudget(budget);
    setIsAddModalOpen(true);
    } catch (error) {
      console.error('Error editing budget:', error);
      toast.error('Failed to edit budget due to invalid data');
    }
  };

  const handleDelete = async (budget: Budget) => {
    if (!user?.id) return;
    try {
      await deleteBudget(user.id, budget.id);
      toast.success("Budget deleted successfully");
    } catch (error) {
      toast.error("Failed to delete budget");
      console.error('Error deleting budget:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      categoryId: '',
      category: '',
      amount: 0,
      period: 'monthly',
      currency: defaultCurrency,
      startDate: new Date(),
      endDate: endOfMonth(new Date()),
    });
  };

  const renderBudgetCard = (budget: ExtendedBudget) => {
    const calculation = budgetCalculations[budget.id];
    
    // Get category ID from the budget
    const categoryId = budget.category_id || budget.categoryId;
    
    // Find the category and its parent if it's a subcategory
    let selectedCategory = null;
    let parentCategory = null;
    let isOverallBudget = false;

    // Handle overall budget (no category specified)
    if (!categoryId) {
      isOverallBudget = true;
    } else {
      // Search through categories to find the selected one
      for (const category of categories) {
        if (category.id === categoryId) {
          selectedCategory = category;
          break;
        }
        // Check subcategories
        if (category.subcategories) {
          const subcategory = category.subcategories.find(sub => sub.id === categoryId);
          if (subcategory) {
            selectedCategory = subcategory;
            parentCategory = category;
            break;
          }
        }
      }
    }
    
    // Skip if no calculation is available
    if (!calculation) return null;
    
    // Skip if category not found and it's not an overall budget
    if (!selectedCategory && !isOverallBudget) return null;
    
    // Get the category name and color
    const categoryName = isOverallBudget
      ? budget.name || 'Overall Budget'
      : parentCategory 
        ? `${parentCategory.name} → ${selectedCategory!.name}`
        : selectedCategory!.name;
    const categoryColor = isOverallBudget
      ? '#6366F1' // Default color for overall budgets
      : selectedCategory!.color || (parentCategory ? parentCategory.color : '#A3A3A3');
    
    return (
      <Card key={budget.id} className="mb-4">
        <CardContent className="p-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-2">
                <div
                  className="w-4 h-4 rounded-full"
                style={{ backgroundColor: categoryColor }}
                />
              <h3 className="text-lg font-semibold">{categoryName}</h3>
            </div>
            <div className="flex items-center space-x-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleEdit(budget)}
              >
                <Pencil className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleDelete(budget)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <div className="mt-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span>Period:</span>
              <span>{calculation.periodLabel}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span>Budget:</span>
              <span>{formatAmount(budget.amount, budget.currency)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span>Spent:</span>
              <span>{formatAmount(calculation.spent, budget.currency)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span>Remaining:</span>
              <span className={calculation.remaining < 0 ? 'text-red-500' : 'text-green-500'}>
                {formatAmount(calculation.remaining, budget.currency)}
              </span>
            </div>
          </div>

          <div className="mt-4 space-y-2">
            <div className="flex justify-between items-center text-sm mb-1">
              <span className="text-[#2F2F2F] dark:text-[#A3A3A3]">Progress</span>
              <span 
                className={cn(
                  "font-medium",
                  calculation.status === 'danger'
                    ? 'text-red-500 dark:text-red-400'
                    : calculation.status === 'warning'
                    ? 'text-yellow-500 dark:text-yellow-400'
                    : 'text-green-500 dark:text-green-400'
                )}
              >
                {Math.round(calculation.percentage)}%
              </span>
            </div>
            <div className="relative w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div
            className={cn(
                  "transition-all duration-500 ease-in-out absolute top-0 left-0 h-full rounded-full",
              calculation.status === 'danger'
                    ? 'bg-gradient-to-r from-red-500 to-red-600 dark:from-red-600 dark:to-red-700'
                : calculation.status === 'warning'
                    ? 'bg-gradient-to-r from-yellow-400 to-yellow-500 dark:from-yellow-500 dark:to-yellow-600'
                    : 'bg-gradient-to-r from-green-400 to-green-500 dark:from-green-500 dark:to-green-600'
            )}
                style={{ width: `${Math.min(100, calculation.percentage)}%` }}
          />
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  // Filter budgets based on search query and period filter
  const filteredBudgets = React.useMemo(() => {
    if (!budgets || !Array.isArray(budgets)) return [];
    
    return budgets.filter(budget => {
      let matchesSearch = true;
      let matchesPeriod = true;
      
      // Apply search filter if provided
      if (searchQuery) {
        // Search by category name or budget name for overall budgets
        const categoryId = budget.category_id;
        let categoryName = '';
        
        // Handle overall budget (no category specified)
        if (!categoryId) {
          categoryName = budget.name || 'Overall Budget';
        } else {
          // Find category name
          for (const category of categories) {
            if (category.id === categoryId) {
              categoryName = category.name;
              break;
            }
            // Check subcategories
            if (category.subcategories) {
              const subcategory = category.subcategories.find(sub => sub.id === categoryId);
              if (subcategory) {
                categoryName = `${category.name} - ${subcategory.name}`;
                break;
              }
            }
          }
        }
        
        matchesSearch = categoryName.toLowerCase().includes(searchQuery.toLowerCase());
      }
      
      // Apply period filter if provided
      if (periodFilter) {
        matchesPeriod = budget.period === periodFilter;
      }
      
      return matchesSearch && matchesPeriod;
    });
  }, [budgets, categories, searchQuery, periodFilter]);

  // Handle create budget button click
  const handleCreateBudgetClick = () => {
    if (onCreateBudget) {
      onCreateBudget();
    } else {
      setIsAddModalOpen(true);
    }
  };

  return (
    <div className="flex flex-col h-full">
      {!hideHeader && (
        <div className="flex flex-row items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold bg-gradient-to-r from-primary to-secondary dark:from-primary-dark dark:to-secondary-dark bg-clip-text text-transparent">
              Budget Management
            </h1>
            <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
              Track and manage your spending limits
            </p>
          </div>
          <div className="flex items-center gap-3">
            {onOpenSmartAssistant && (
              <Button
                variant="outline"
                className="border-blue-200 text-blue-600 hover:bg-blue-50 dark:border-blue-800 dark:text-blue-400 dark:hover:bg-blue-950/50 shadow-sm rounded-xl"
                onClick={onOpenSmartAssistant}
              >
                <Brain className="mr-2 h-4 w-4" />
                Smart Assistant
              </Button>
            )}
            <Button
              variant="default"
              className="bg-secondary hover:bg-secondary/90 text-white dark:bg-primary-dark dark:hover:bg-primary-dark/90 shadow-sm rounded-xl"
              onClick={handleCreateBudgetClick}
            >
              <Plus className="mr-2 h-4 w-4" />
              Add Budget
            </Button>
          </div>
        </div>
      )}

      {/* Display budgets */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 overflow-auto">
        {filteredBudgets.length === 0 ? (
          <div className="col-span-full">
            <Card className="p-8 text-center bg-gradient-to-br from-blue-50/50 to-indigo-50/50 dark:from-blue-950/20 dark:to-indigo-950/20 border-blue-200 dark:border-blue-800">
              <CardContent className="p-0">
                <div className="mx-auto w-16 h-16 mb-4 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center">
                  <Brain className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                  No budgets yet
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 max-w-md mx-auto">
                  Create your first budget to start tracking your spending. Use our Smart Assistant for personalized recommendations!
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                  {onOpenSmartAssistant && (
                    <Button
                      variant="default"
                      className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white shadow-sm rounded-xl"
                      onClick={onOpenSmartAssistant}
                    >
                      <Brain className="mr-2 h-4 w-4" />
                      Try Smart Assistant
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    className="border-gray-200 text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-800 rounded-xl"
                    onClick={handleCreateBudgetClick}
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Create Manual Budget
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        ) : (
          filteredBudgets.map(budget => renderBudgetCard(budget))
        )}
      </div>

      {/* Add/Edit Budget Dialog */}
      <Dialog open={isAddModalOpen} onOpenChange={setIsAddModalOpen}>
        <DialogContent className="sm:max-w-[500px] bg-background-secondary/95 dark:bg-background-dark-secondary/95 backdrop-blur-xl backdrop-saturate-150 border border-border dark:border-border-dark rounded-xl shadow-lg">
          <DialogHeader className="px-6 pt-6">
            <DialogTitle className="text-xl font-bold bg-gradient-to-r from-primary to-secondary dark:from-primary-dark dark:to-secondary-dark bg-clip-text text-transparent">
              {editingBudget ? 'Edit Budget' : 'Create New Budget'}
            </DialogTitle>
            <DialogDescription className="text-sm text-text-secondary dark:text-text-dark-secondary mt-1">
              {editingBudget ? 'Update your budget settings below.' : 'Set up a new budget to track your spending.'}
            </DialogDescription>
          </DialogHeader>
          
          <ScrollArea className="max-h-[70vh]">
            <form onSubmit={handleSubmit} className="px-6 py-4 space-y-6">
              <div className="space-y-5">
                {/* Category Selector */}
                <div className="space-y-2">
                  <Label htmlFor="category" className="text-sm font-medium text-text-primary dark:text-text-dark-primary">
                    Category
                  </Label>
                  <div className="relative">
                    <Combobox
                      as="div"
                      value={formData.categoryId}
                      onChange={(categoryId: string) => {
                        const category = categories.find(c => c.id === categoryId);
                        setFormData(prev => ({
                          ...prev,
                          categoryId,
                          category: category?.name || ''
                        }));
                        setIsCategoryOpen(false);
                      }}
                      className="relative z-[60]"
                    >
                      <div className="relative">
                        <div
                          className="flex items-center bg-background-input dark:bg-background-dark-input border border-border dark:border-border-dark rounded-xl px-3 h-11 cursor-pointer hover:border-secondary dark:hover:border-primary-dark transition-colors"
                          onClick={() => setIsCategoryOpen(!isCategoryOpen)}
                        >
                          <Tag className="w-4 h-4 text-text-secondary dark:text-text-dark-secondary mr-2" />
                          <Combobox.Input
                            className="bg-transparent flex-1 outline-none border-none rounded-none focus:ring-0 focus:outline-none shadow-none focus:shadow-none text-text-primary dark:text-text-dark-primary placeholder-text-secondary/60 dark:placeholder-text-dark-secondary/60 appearance-none"
                            placeholder="Select category..."
                            onClick={(e) => {
                              e.stopPropagation();
                              setIsCategoryOpen(true);
                            }}
                            onChange={(e) => {
                              e.stopPropagation();
                              setCategoryQuery(e.target.value);
                              setIsCategoryOpen(true);
                            }}
                            displayValue={(categoryId: string) => {
                              if (!categoryId) return '';
                              
                              // Find the category or subcategory with this ID
                              for (const category of categories) {
                                // Check if this is a main category
                                if (category.id === categoryId) {
                                  return category.name;
                                }
                                
                                // Check if this is a subcategory
                                const subcategory = category.subcategories?.find(sub => sub.id === categoryId);
                                if (subcategory) {
                                  return `${category.name} → ${subcategory.name}`;
                                }
                              }
                              
                              return formData.category || '';
                            }}
                          />
                          <ChevronDown 
                            className="w-4 h-4 text-text-secondary dark:text-text-dark-secondary ml-2"
                            onClick={(e) => {
                              e.stopPropagation();
                              setIsCategoryOpen(!isCategoryOpen);
                            }}
                          />
                        </div>

                        {isCategoryOpen && (
                          <Combobox.Options
                            className="absolute z-[1000] mt-1 max-h-60 w-full overflow-auto rounded-xl bg-background-secondary dark:bg-background-dark-secondary backdrop-blur-sm backdrop-saturate-150 border border-border dark:border-border-dark py-1 shadow-lg focus:outline-none text-sm"
                            static
                          >
                            {categories.length === 0 ? (
                              <div className="py-2 px-4 text-sm text-text-secondary dark:text-text-dark-secondary">
                                No categories found
                              </div>
                            ) : (
                              categories
                                .filter(category => category.type === 'expense')
                                .filter(category => {
                                  if (!categoryQuery) return true;
                                  const query = categoryQuery.toLowerCase();
                                  return category.name.toLowerCase().includes(query) || 
                                    (category.subcategories?.some(sub => 
                                      sub.name.toLowerCase().includes(query)
                                    ) || false);
                                })
                                .map((category) => (
                                <Fragment key={category.id}>
                                  {/* Parent Category */}
                                  <Combobox.Option
                                    value={category.id}
                                    className={({ active }) =>
                                      `relative cursor-pointer select-none py-2 pl-4 pr-9 ${
                                        active
                                          ? 'bg-secondary/10 text-secondary dark:bg-primary-dark/10 dark:text-primary-dark'
                                          : 'text-text-primary dark:text-text-dark-primary'
                                      }`
                                    }
                                  >
                                    {({ selected }) => (
                                      <>
                                        <div className="flex items-center">
                                          <div
                                            className="w-2 h-2 rounded-full mr-2"
                                            style={{ backgroundColor: category.color }}
                                          />
                                          <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                                            {category.name}
                                          </span>
                                        </div>
                                        {selected && (
                                          <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-secondary dark:text-primary-dark">
                                            <Check className="h-5 w-5" aria-hidden="true" />
                                          </span>
                                        )}
                                      </>
                                    )}
                                  </Combobox.Option>

                                  {/* Subcategories */}
                                  {category.subcategories?.map((subcategory) => (
                                    <Combobox.Option
                                      key={subcategory.id}
                                      value={subcategory.id}
                                      className={({ active }) =>
                                        `relative cursor-pointer select-none py-2 pl-8 pr-9 ${
                                          active
                                            ? 'bg-secondary/10 text-secondary dark:bg-primary-dark/10 dark:text-primary-dark'
                                            : 'text-text-primary dark:text-text-dark-primary'
                                        }`
                                      }
                                    >
                                      {({ selected }) => (
                                        <>
                                          <div className="flex items-center">
                                            <div
                                              className="w-1.5 h-1.5 rounded-full mr-2"
                                              style={{ backgroundColor: category.color }}
                                            />
                                            <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                                              {subcategory.name}
                                            </span>
                                          </div>
                                          {selected && (
                                            <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-secondary dark:text-primary-dark">
                                              <Check className="h-4 w-4" aria-hidden="true" />
                                            </span>
                                          )}
                                        </>
                                      )}
                                    </Combobox.Option>
                                  ))}
                                </Fragment>
                              ))
                            )}
                          </Combobox.Options>
                        )}
                      </div>
                    </Combobox>
                  </div>
                </div>

                <Separator className="bg-border dark:bg-border-dark" />

                <div className="grid grid-cols-2 gap-4">
                  {/* Amount Field */}
                  <div className="space-y-2">
                    <Label htmlFor="amount" className="text-sm font-medium text-text-primary dark:text-text-dark-primary">
                      Amount
                    </Label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-text-secondary dark:text-text-dark-secondary" />
                      <Input
                        id="amount"
                        type="number"
                        value={formData.amount}
                        onChange={(e) => setFormData({ ...formData, amount: parseFloat(e.target.value) || 0 })}
                        className="pl-10 h-11 rounded-xl border border-border dark:border-border-dark bg-background-input dark:bg-background-dark-input focus:ring-2 focus:ring-secondary dark:focus:ring-primary-dark focus:ring-offset-0"
                      />
                    </div>
                  </div>

                  {/* Currency Selector */}
                  <div className="space-y-2">
                    <Label htmlFor="currency" className="text-sm font-medium text-text-primary dark:text-text-dark-primary">
                      Currency
                    </Label>
                    <Select
                      value={formData.currency}
                      onValueChange={(value) => setFormData({ ...formData, currency: value })}
                    >
                      <SelectTrigger className="h-11 rounded-xl border border-border dark:border-border-dark bg-background-input dark:bg-background-dark-input focus:ring-2 focus:ring-secondary dark:focus:ring-primary-dark focus:ring-offset-0">
                        <SelectValue placeholder="Select currency" />
                      </SelectTrigger>
                      <SelectContent className="bg-background-secondary dark:bg-background-dark-secondary backdrop-blur-sm backdrop-saturate-150 border border-border dark:border-border-dark rounded-xl">
                        <ScrollArea className="h-[200px]">
                          {currencies.length > 0 ? (
                            currencies.map((currency) => (
                              <SelectItem key={currency.id} value={currency.code}>
                                {currency.symbol} {currency.code} - {currency.name}
                              </SelectItem>
                            ))
                          ) : (
                            <SelectItem value={defaultCurrency}>
                              {defaultCurrency}
                            </SelectItem>
                          )}
                        </ScrollArea>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <Separator className="bg-border dark:bg-border-dark" />

                {/* Budget Period Selector */}
                <div className="space-y-2">
                  <Label htmlFor="period" className="text-sm font-medium text-text-primary dark:text-text-dark-primary">
                    Budget Period
                  </Label>
                  <Select
                    value={formData.period}
                    onValueChange={(value: BudgetPeriod) => setFormData({ ...formData, period: value })}
                  >
                    <SelectTrigger className="h-11 rounded-xl border border-border dark:border-border-dark bg-background-input dark:bg-background-dark-input focus:ring-2 focus:ring-secondary dark:focus:ring-primary-dark focus:ring-offset-0">
                      <SelectValue placeholder="Select period" />
                    </SelectTrigger>
                    <SelectContent className="bg-background-secondary dark:bg-background-dark-secondary backdrop-blur-sm backdrop-saturate-150 border border-border dark:border-border-dark rounded-xl">
                      <SelectItem key="daily" value="daily">Daily</SelectItem>
                      <SelectItem key="weekly" value="weekly">Weekly</SelectItem>
                      <SelectItem key="monthly" value="monthly">Monthly</SelectItem>
                      <SelectItem key="yearly" value="yearly">Yearly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Date Range Fields */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="startDate" className="text-sm font-medium text-text-primary dark:text-text-dark-primary">
                      Start Date
                    </Label>
                    <Input
                      id="startDate"
                      type="date"
                      value={(() => {
                        try {
                          return formData.startDate.toISOString().split('T')[0];
                        } catch (error) {
                          console.warn('Invalid start date', error);
                          return new Date().toISOString().split('T')[0];
                        }
                      })()}
                      onChange={(e) => {
                        try {
                          const newDate = new Date(e.target.value);
                          if (isNaN(newDate.getTime())) {
                            toast.error('Invalid date format');
                            return;
                          }
                          setFormData({ ...formData, startDate: newDate });
                        } catch (error) {
                          console.error('Error setting start date:', error);
                          toast.error('Invalid date format');
                        }
                      }}
                      className="h-11 rounded-xl border border-border dark:border-border-dark bg-background-input dark:bg-background-dark-input focus:ring-2 focus:ring-secondary dark:focus:ring-primary-dark focus:ring-offset-0"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="endDate" className="text-sm font-medium text-text-primary dark:text-text-dark-primary">
                      End Date
                    </Label>
                    <Input
                      id="endDate"
                      type="date"
                      value={(() => {
                        try {
                          return formData.endDate.toISOString().split('T')[0];
                        } catch (error) {
                          console.warn('Invalid end date', error);
                          return new Date().toISOString().split('T')[0];
                        }
                      })()}
                      onChange={(e) => {
                        try {
                          const newDate = new Date(e.target.value);
                          if (isNaN(newDate.getTime())) {
                            toast.error('Invalid date format');
                            return;
                          }
                          setFormData({ ...formData, endDate: newDate });
                        } catch (error) {
                          console.error('Error setting end date:', error);
                          toast.error('Invalid date format');
                        }
                      }}
                      className="h-11 rounded-xl border border-border dark:border-border-dark bg-background-input dark:bg-background-dark-input focus:ring-2 focus:ring-secondary dark:focus:ring-primary-dark focus:ring-offset-0"
                    />
                  </div>
                </div>
              </div>
            </form>
          </ScrollArea>

          <DialogFooter className="px-6 py-4 border-t border-border dark:border-border-dark bg-background-secondary/50 dark:bg-background-dark-secondary/50 backdrop-blur-sm backdrop-saturate-150 rounded-b-xl">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setIsAddModalOpen(false);
                setEditingBudget(null);
                resetForm();
              }}
              className="h-11 px-6 rounded-xl border-secondary dark:border-primary-dark text-secondary dark:text-primary-dark hover:bg-secondary/10 dark:hover:bg-primary-dark/10"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              onClick={handleSubmit}
              className="h-11 px-6 rounded-xl bg-primary hover:bg-primary/90 text-white dark:bg-primary-dark dark:hover:bg-primary-dark/90 shadow-sm hover:shadow-md transition-all duration-200"
            >
              {editingBudget ? 'Update' : 'Create'} Budget
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}